import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
  CreateMembershipVariantDto,
  MembershipVariantDto,
  UserMembershipTypeDto,
} from '../../../models/user-membership/dto/user-membership.dto';
import { GET_USER_MEMBERSHIP_TYPES_QUERY_KEY } from './useGetUserMembershipTypes';

/**
 * POST user-membership-types/:typeId/variants — no :orgId segment on the
 * route itself (api/CLAUDE.md tenancy note: the org is derived from the
 * loaded :typeId row server-side, never from a caller-supplied org id — see
 * assertAdminAccessToMembershipType in user-membership-type.controller.ts).
 * `organizationId` here is only used to locate the right cached type list to
 * patch on success, same shape useUpdateMembershipTypeCgu already uses.
 */
export const useCreateMembershipVariant = (
  organizationId: string,
  membershipTypeId: string,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createMembershipVariant', membershipTypeId],
    mutationFn: (body: CreateMembershipVariantDto) =>
      apiClient.fetchApi<MembershipVariantDto, CreateMembershipVariantDto>({
        method: 'POST',
        endpoint: `user-membership-types/${membershipTypeId}/variants`,
        body,
      }),
    onSuccess: (created) => {
      queryClient.setQueryData<UserMembershipTypeDto[]>(
        GET_USER_MEMBERSHIP_TYPES_QUERY_KEY(organizationId),
        (previous) => {
          if (!previous) return previous;
          return previous.map((type) => {
            if (type.id !== membershipTypeId) return type;
            const existingVariants = type.variants ?? [];
            // The server enforces "at most one live default per type"
            // (uq_membership_variant_default_live) by demoting whichever
            // variant currently holds it — mirror that locally so the list
            // doesn't show two defaults until the next refetch.
            const nextVariants = created.isDefault
              ? existingVariants.map((v) => ({ ...v, isDefault: false }))
              : existingVariants;
            return { ...type, variants: [...nextVariants, created] };
          });
        },
      );
    },
  });
};
